import { Text, VStack } from "@chakra-ui/layout";
import { Trans, t } from "@lingui/macro";
import type { SortOrder as GraphqlSortOrder } from "@src/__generated__/graphql";
import type {
  BrandRevenueOrderByColumnEnum,
  SortOrder as UrqlSortOrder,
} from "@src/__generated__/urql-graphql";
import { ACTIONS_COLUMN_ID, DataTable } from "@src/components/ui-kit";
import { percent } from "@src/utils/formatters";
import { lowerCase } from "lodash";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { ProfitAndRevenueAmount } from "../../../common/components";
import { createBrandColumns, withBrandHeaderTooltip } from "../../common";
import type {
  BrandRevenueReportColumn,
  BrandRevenueReportRow,
} from "../../common/types";
import { BrandProfitModel } from "../../profit/models";
import { useStore } from "../store";

export const Table = observer(function Table() {
  const store = useStore();
  const columns = useMemo<BrandRevenueReportColumn[]>(() => {
    const cols = createBrandColumns<BrandRevenueReportRow>({
      detailPathname: "/reports/invoiced-by-project",
      from: store.reportStore.dateRange.start,
      to: store.reportStore.dateRange.end,
      groupBy: store.reportStore.groupBy,
    });

    cols.push({
      id: "_ourWorkIncome",
      meta: { label: t`Work invoiced`, align: "right" },
      header: withBrandHeaderTooltip(
        t`Work invoiced`,
        <Trans>Invoiced amount of Work Budget Items (without VAT)</Trans>,
      ),
      cell: ({ row }) => {
        if (row.original instanceof BrandProfitModel) {
          return null;
        }

        return (
          <ProfitAndRevenueAmount
            amount={row.original.our_work_billings_for_date_range}
          />
        );
      },
    });

    store.reportStore.expenseBudgetCategories.forEach((expCategory) => {
      cols.push({
        id: `billings_${expCategory.id}`,
        meta: {
          label: `${expCategory.name} ${t`Invoiced`}`,
          align: "right",
        },
        header: withBrandHeaderTooltip(
          `${expCategory.name} ${t`Invoiced`}`,
          t`Invoiced amount of ${lowerCase(expCategory.name)} Budget Items (without VAT)`,
        ),
        cell: ({ row }) => {
          if (row.original instanceof BrandProfitModel) {
            return null;
          }

          const billings = row.original.expenses_billings_for_date_range.find(
            (category) =>
              category.expense_budget_category_id === expCategory.id,
          )?.billings;

          return <ProfitAndRevenueAmount amount={billings ?? 0} />;
        },
      });
    });

    cols.push(
      {
        id: "_ourGrossIncome",
        meta: { label: t`Gross Income`, align: "right" },
        header: withBrandHeaderTooltip(
          t`Gross Income`,
          <Trans>
            Sum of the Work invoiced, Media Actual Profit, and Production Actual
            Profit.
          </Trans>,
        ),
        cell: ({ row }) => (
          <ProfitAndRevenueAmount
            amount={row.original.gross_income_for_date_range}
          />
        ),
      },
      {
        id: "total_invoiced",
        meta: { label: t`Total Invoiced`, align: "right" },
        header: withBrandHeaderTooltip(
          t`Total Invoiced`,
          <Trans>
            Sum of the invoiced amount for Work, Media and Production
          </Trans>,
        ),
        cell: ({ row }) => {
          if (row.original instanceof BrandProfitModel) {
            return null;
          }

          return (
            <ProfitAndRevenueAmount amount={row.original.total_invoiced} />
          );
        },
      },
      {
        id: "invoiced-share",
        meta: { label: t`Share Invoiced (%)`, align: "right" },
        header: withBrandHeaderTooltip(
          t`Share Invoiced (%)`,
          <Trans>
            Percentage of total invoicing for the selected period and applied
            filters.
          </Trans>,
        ),
        cell: ({ row }) => {
          if (row.original instanceof BrandProfitModel) {
            return null;
          }

          return (
            <VStack align="right" pr="5">
              <Text>{percent.format(row.original.filtered_revenue_share)}</Text>
              <Text color="gray.400">
                {percent.format(row.original.total_revenue_share)}
              </Text>
            </VStack>
          );
        },
      },
      {
        id: ACTIONS_COLUMN_ID,
      },
    );

    return cols;
  }, [
    store.reportStore.dateRange.end,
    store.reportStore.dateRange.start,
    store.reportStore.expenseBudgetCategories,
    store.reportStore.groupBy,
  ]);

  const orderedBy = store.reportStore.orderBy.clauses[0];

  return (
    <DataTable.Body<BrandRevenueReportRow, BrandRevenueOrderByColumnEnum>
      tableKey={store.key}
      data={store.brands as BrandRevenueReportRow[]}
      columns={columns}
      orderedBy={
        orderedBy
          ? (() => {
              const order = orderedBy.order as GraphqlSortOrder | undefined;

              if (!order) {
                return undefined;
              }

              return {
                column: orderedBy.column,
                order,
              };
            })()
          : undefined
      }
      onOrderByChange={(clause) => {
        if (!clause.order) {
          store.reportStore.orderBy.setClauses([]);
          return;
        }

        const order = clause.order as UrqlSortOrder | undefined;

        if (!order) {
          store.reportStore.orderBy.setClauses([]);
          return;
        }

        store.reportStore.orderBy.setClauses([
          { column: clause.column, order },
        ]);
      }}
    />
  );
});
